import {
  Badge,
  Box,
  Button,
  Center,
  Collapse,
  Flex,
  HStack,
  Icon,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronDown, FiDownload, FiFile, FiRepeat } from "react-icons/fi";
import {
  BidV2Status,
  NegotiationRoundStatus,
  bffApi,
  useGetPortalNegotiationRoundsQuery,
  type PortalDocumentDto,
  type PortalNegotiationRoundDto,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useDisplayDate } from "../../../../common/formatting/useDisplayDate";
import { TipTap } from "../../../../common/input/TipTap/TipTap";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { PrivateBidCta } from "./PrivateBidCta";
import { roundAcceptsBids } from "./roundAcceptsBids";
import { SectionCard } from "./SectionCard";
import { portalEventMayHaveRounds } from "./useOpenPortalRound";

interface Props {
  event: PortalSourcingEventDetailDto;
}

export const PortalNegotiationRoundsSection = ({ event }: Props) => {
  const { t } = useTranslation();

  // The server returns only the rounds this organization is invited to, so it decides who sees
  // what. A private invitee is a participant from the moment they are invited unless they decline;
  // a public one is a participant by way of the § 23-2 invitation the round was built from.
  const mayHaveRounds = portalEventMayHaveRounds(event);

  const { data, isLoading, isError } = useGetPortalNegotiationRoundsQuery(
    { eventId: event.id },
    { skip: !mayHaveRounds },
  );

  if (!mayHaveRounds) return null;

  const rounds = data?.rounds ?? [];
  const visibleRounds = rounds.filter(
    (r) => r.status === NegotiationRoundStatus.Published || r.status === NegotiationRoundStatus.Completed,
  );

  if (!isLoading && !isError && visibleRounds.length === 0) return null;

  return (
    <SectionCard icon={FiRepeat} title={t("Negotiation rounds")} count={visibleRounds.length}>
      {isLoading ? (
        <Center py="8">
          <Spinner color="smPrimary" />
        </Center>
      ) : isError ? (
        <Center py="8">
          <Text color="smError">{t("Could not load negotiation rounds.")}</Text>
        </Center>
      ) : (
        <Stack spacing="4">
          <LatestRoundBanner rounds={visibleRounds} event={event} />
          <Stack spacing="3">
            {visibleRounds.map((round, idx) => (
              <PortalRoundRow
                key={round.id}
                round={round}
                event={event}
                defaultExpanded={idx === visibleRounds.length - 1 && round.status === NegotiationRoundStatus.Published}
              />
            ))}
          </Stack>
        </Stack>
      )}
    </SectionCard>
  );
};

interface LatestRoundBannerProps {
  rounds: PortalNegotiationRoundDto[];
  event: PortalSourcingEventDetailDto;
}

const LatestRoundBanner = ({ rounds, event }: LatestRoundBannerProps) => {
  const { t } = useTranslation();
  const latestPublished = rounds.filter((r) => roundAcceptsBids(r)).at(-1);

  if (!latestPublished) return null;

  return (
    <Box bg="smPrimarySofter" rounded="lg" px="4" py="3" border="1px solid" borderColor="smPrimary">
      <Flex align="center" justify="space-between" flexWrap="wrap" gap="3">
        <Stack spacing="0">
          <Text fontWeight="semibold" fontSize="sm">
            {t("Latest open round")}: {t("Round")} {latestPublished.roundNumber}
            {latestPublished.isFinalRound ? ` — ${t("Final Round")}` : ""}
          </Text>
          {latestPublished.title && (
            <Text fontSize="sm" color="smMuted">
              {latestPublished.title}
            </Text>
          )}
        </Stack>
        <PrivateBidCta event={event} round={latestPublished} variant="compact" />
      </Flex>
    </Box>
  );
};

interface PortalRoundRowProps {
  round: PortalNegotiationRoundDto;
  event: PortalSourcingEventDetailDto;
  defaultExpanded: boolean;
}

const PortalRoundRow = ({ round, event, defaultExpanded }: PortalRoundRowProps) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const acceptsBids = roundAcceptsBids(round);
  const deadlinePassedWhileOpen = round.status === NegotiationRoundStatus.Published && !acceptsBids;

  return (
    <Box
      border="1px solid"
      borderColor={isExpanded ? "smPrimary" : "smBorder"}
      rounded="lg"
      overflow="hidden"
      bg="smBackground"
      transition="border-color 0.15s"
    >
      <Flex
        px="4"
        py="3"
        align="center"
        justify="space-between"
        cursor="pointer"
        _hover={{ bg: "smBackgroundSecondary" }}
        onClick={() => setIsExpanded((prev) => !prev)}
        role="button"
        aria-expanded={isExpanded}
      >
        <HStack spacing="3">
          <Text fontWeight="semibold" fontSize="sm">
            {t("Round")} {round.roundNumber}
            {round.isFinalRound ? ` — ${t("Final Round")}` : ""}
          </Text>
          <RoundStatusBadge round={round} />
        </HStack>
        <HStack spacing="3">
          <Text fontSize="sm" color="smMuted">
            {t("Deadline")}: {displayDate(round.deadline)}
          </Text>
          <Icon
            as={FiChevronDown}
            boxSize="4"
            color="smMuted"
            transform={isExpanded ? "rotate(180deg)" : undefined}
            transition="transform 0.15s"
          />
        </HStack>
      </Flex>

      <Collapse in={isExpanded} animateOpacity>
        <Stack spacing="4" px="4" pb="4" pt="2" borderTop="1px solid" borderColor="smBorder">
          {round.title && (
            <Text fontWeight="medium" fontSize="sm">
              {round.title}
            </Text>
          )}
          {round.description && (
            <TipTap content={round.description} editable={false} hasBorder={false} padding="0" maxHeight="" />
          )}
          {round.documents.length > 0 && (
            <Stack spacing="2">
              <Text fontSize="sm" fontWeight="medium" color="smText">
                {t("Documents")} ({round.documents.length})
              </Text>
              <RoundDocumentList eventId={event.id} documents={round.documents} />
            </Stack>
          )}
          {acceptsBids && round.isCallerInvited && (
            <Box pt="1">
              <PrivateBidCta event={event} round={round} variant="full" />
            </Box>
          )}
          {deadlinePassedWhileOpen && round.isCallerInvited && (
            <Text fontSize="sm" color="smMuted">
              {round.myBid?.status === BidV2Status.Submitted
                ? t("The deadline for this round has passed. Your bid was submitted.")
                : t("The deadline for this round has passed. You did not submit a bid in this round.")}
            </Text>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};

interface RoundDocumentListProps {
  eventId: string;
  documents: PortalDocumentDto[];
}

const RoundDocumentList = ({ eventId, documents }: RoundDocumentListProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { trigger } = useApiError();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const handleDownload = async (documentId: string) => {
    setPendingId(documentId);
    try {
      const result = await dispatch(
        bffApi.endpoints.getPortalDocumentDownloadLink.initiate({ eventId, documentId }),
      ).unwrap();
      window.open(result.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      trigger(e);
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Stack spacing="2">
      {documents.map((doc) => (
        <HStack
          key={doc.id}
          borderWidth="1px"
          borderColor="smBorder"
          rounded="md"
          px="3"
          py="2"
          bg="smBackground"
          justify="space-between"
          align="center"
          spacing="3"
        >
          <HStack spacing="3" align="center" flex="1" minW="0">
            <Box color="smMuted" flexShrink="0">
              <FiFile size={16} />
            </Box>
            <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
              {doc.name}
            </Text>
          </HStack>
          <Button
            size="xs"
            variant="outline"
            colorScheme="gray"
            leftIcon={<FiDownload />}
            onClick={() => handleDownload(doc.id)}
            isLoading={pendingId === doc.id}
            flexShrink="0"
          >
            {t("Download")}
          </Button>
        </HStack>
      ))}
    </Stack>
  );
};

const RoundStatusBadge = ({ round }: { round: PortalNegotiationRoundDto }) => {
  const { t } = useTranslation();
  if (round.status === NegotiationRoundStatus.Published && !roundAcceptsBids(round)) {
    return (
      <Badge colorScheme="orange" fontSize="xs">
        {t("Deadline passed")}
      </Badge>
    );
  }
  const colorMap: Record<NegotiationRoundStatus, string> = {
    [NegotiationRoundStatus.Draft]: "gray",
    [NegotiationRoundStatus.Published]: "green",
    [NegotiationRoundStatus.Completed]: "blue",
  };
  const labelMap: Record<NegotiationRoundStatus, string> = {
    [NegotiationRoundStatus.Draft]: t("Draft"),
    [NegotiationRoundStatus.Published]: t("Open"),
    [NegotiationRoundStatus.Completed]: t("Closed"),
  };
  return (
    <Badge colorScheme={colorMap[round.status]} fontSize="xs">
      {labelMap[round.status]}
    </Badge>
  );
};
